import { PwaInstallButtonComponent } from '../../core/components/pwa-install-button/pwa-install-button.component';
import {
  Component,
  OnInit,
  effect,
  inject
} from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { RouterOutlet } from '@angular/router';
import {
  AlertModule,
  AlertService,
  SearchComponent,
  SharedSidePanel,
  VexConfigService,
  VexLayoutService,
  VexSidebarComponent
} from '@pharvio/core/ui';
import { TenantContextService } from '../../core/services/tenant-context.service';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { AsyncPipe } from '@angular/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { Store } from '@ngxs/store';
import { ManuallyUpdateBranches } from '@pharvio/branches/data-access';
import {
  CURRENCY_CODE_TO_SYMBOL_MAP,
  hasPermission,
  IBranch,
  OfflineModeService
} from '@pharvio/core/shared';
import {
  OfflineStatusPillComponent,
  StaleSessionBannerComponent
} from '@pharvio/offline';
import {
  ConfigPanelComponent,
  ConfigPanelToggleComponent,
  QuickpanelComponent,
  SidenavComponent,
  SidePanelDirective,
  ToolbarComponent,
  VexConfig,
  VexProgressBarComponent
} from '@pharvio/core/ui';
import {
  LoadStorefrontSettings,
  StorefrontSettingsState
} from '@pharvio/storefront/data-access';
import { ConnectionStrengthIndicatorComponent } from '../../core/components/connection-strength-indicator/connection-strength-indicator.component';
import { OfflineBannerComponent } from '../../core/components/offline-banner/offline-banner.component';
import { AvailabilityStatusPillComponent } from '../../features/storefront/components/availability-status-pill/availability-status-pill.component';
import { BaseLayoutComponent } from '../base-layout/base-layout.component';
import {
  AuthState,
  LoadMyBranches
} from '@pharvio/accounts/admin-account/data-access';
import { SetCurrency } from '@pharvio/currency/data-access';
import { PERMISSIONS } from '../../core/constants/permissions.constant';
import { LoadFeatures, LoadTaxClasses } from '@pharvio/settings/data-access';

@Component({
  selector: 'vex-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  imports: [
    BaseLayoutComponent,
    AsyncPipe,
    SidenavComponent,
    ToolbarComponent,
    QuickpanelComponent,
    ConfigPanelToggleComponent,
    VexSidebarComponent,
    ConfigPanelComponent,
    MatDialogModule,
    MatSidenavModule,
    RouterOutlet,
    SearchComponent,
    VexProgressBarComponent,
    SharedSidePanel,
    SidePanelDirective,
    AvailabilityStatusPillComponent,
    ConnectionStrengthIndicatorComponent,
    OfflineBannerComponent,
    OfflineStatusPillComponent,
    PwaInstallButtonComponent,
    StaleSessionBannerComponent,
    AlertModule
  ]
})
export class LayoutComponent implements OnInit {
  private readonly layoutService = inject(VexLayoutService);
  private readonly configService = inject(VexConfigService);
  private readonly store = inject(Store);
  private readonly offlineMode = inject(OfflineModeService);
  private readonly alert = inject(AlertService);
  private readonly dateAdapter = inject(DateAdapter);
  private readonly tenant = inject(TenantContextService);

  /**
   * Keeps every Material datepicker on the tenant's locale.
   *
   * `MAT_DATE_LOCALE` is resolved once at bootstrap, which is too early — the
   * tenant's locale arrives with the storefront settings a moment later. This
   * is Material's supported way to change it afterwards: `setLocale` emits on
   * `localeChanges`, and every open datepicker re-renders. Done here in the
   * shell so it applies to the whole app rather than per page.
   *
   * This covers display *and* typed input: the adapter bound to
   * `DateAdapter` is `LocaleDateAdapter`, which reads the locale's part order
   * from `Intl`, so a hand-typed "01/02/2026" follows the same order the
   * field renders. See `locale-date-adapter.ts`.
   */
  private readonly syncDateLocale = effect(() => {
    this.dateAdapter.setLocale(this.tenant.locale());
  });

  config$: Observable<VexConfig> = this.configService.config$;
  sidenavCollapsed$: Observable<boolean> = this.layoutService.sidenavCollapsed$;
  sidenavDisableClose$: Observable<boolean> = this.layoutService.isDesktop$;
  sidenavFixedInViewport$: Observable<boolean> =
    this.layoutService.isDesktop$.pipe(map((isDesktop) => !isDesktop));
  sidenavMode$: Observable<MatDrawerMode> = combineLatest([
    this.layoutService.isDesktop$,
    this.configService.select((config) => config.layout)
  ]).pipe(
    map(([isDesktop, layout]) =>
      !isDesktop || layout === 'vertical' ? 'over' : 'side'
    )
  );
  sidenavOpen$: Observable<boolean> = this.layoutService.sidenavOpen$;
  configPanelOpen$: Observable<boolean> = this.layoutService.configPanelOpen$;
  quickpanelOpen$: Observable<boolean> = this.layoutService.quickpanelOpen$;

  ngOnInit(): void {
    this.store.dispatch(new LoadMyBranches());

    // The resolved feature map drives nav visibility and catalog wording. It's
    // persisted and fails open, so offline it simply keeps what it had.
    this.store.dispatch(new LoadFeatures());

    // Tax classes load in every mode — the POS prices each line against them,
    // so offline the action reads the device's own copy instead of the API.
    // Gated on READ, not MANAGE: a cashier prices sales without ever being
    // allowed to edit a rate, and that is also the permission the endpoint
    // itself requires.
    this.store
      .select(AuthState.permissions)
      .pipe(take(1))
      .subscribe({
        next: (permissions) => {
          const canReadTaxClasses = hasPermission(permissions, [
            PERMISSIONS.SETTINGS.TAX_CLASSES_READ
          ]);

          if (canReadTaxClasses) {
            this.store.dispatch(new LoadTaxClasses());
          }
        }
      });

    // Offline: storefront settings come from the persisted store slice; a
    // request now would only stall on the retry interceptor.
    if (this.offlineMode.isOffline()) return;
    this.store
      .dispatch(new LoadStorefrontSettings())
      .pipe(
        switchMap(() => this.store.select(StorefrontSettingsState.loading)),
        filter((loading) => !loading),
        take(1)
      )
      .subscribe({
        next: () => {
          const loadError = this.store.selectSnapshot(
            StorefrontSettingsState.error
          );
          if (loadError) {
            // Background load — nothing on screen owns it, so say it here.
            // Storefront links and branch/currency defaults depend on it.
            this.alert.error(
              "Couldn't load storefront settings. Storefront links may be unavailable."
            );
          }

          const storeSettings = this.store.selectSnapshot(
            StorefrontSettingsState.settings
          );

          if (storeSettings) {
            if (storeSettings.branches && storeSettings.branches.length > 0) {
              const branches: IBranch[] = storeSettings.branches.map(
                (branch) => ({
                  id: branch.id,
                  name: branch.name,
                  stateRegion: branch.stateRegion,
                  city: branch.city,
                  countryCode: branch.countryCode,
                  streetAddress: branch.streetAddress,
                  addressDescription: branch.addressDescription ?? '',
                  currency: storeSettings.currency,
                  status: 'active',
                  isDefault: branch.isDefault ?? false,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                  code: branch.code
                })
              );
              this.store.dispatch(new ManuallyUpdateBranches(branches));
              const currency =
                CURRENCY_CODE_TO_SYMBOL_MAP[storeSettings.currency];

              if (currency) {
                // The whole record, not a field-by-field copy: it now also
                // carries `minorUnitDigits`, and rebuilding it by hand would
                // drop that silently.
                this.store.dispatch(new SetCurrency(currency));
              }
            }
          }
        }
      });
  }
  onSidenavClosed(): void {
    this.layoutService.closeSidenav();
  }

  onQuickpanelClosed(): void {
    this.layoutService.closeQuickpanel();
  }
}
